import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Box, Typography, TextField, Button, Alert } from '@mui/material';
import { API_URL } from '../apiConfig';

const ResetPassword = () => {
  const { token, type } = useParams();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState({ error: '', success: '' });
  const [isTokenValid, setIsTokenValid] = useState(true);
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    const validateToken = async () => {
      const endpoint = `${API_URL}/api/${type === 'team_member' ? 'validate_team_member_reset_token' : 'validate_reset_token'}/${token}`;
      try {
        await axios.get(endpoint);
      } catch (error) {
        setMessage({ error: 'Invalid or expired token', success: '' });
        setIsTokenValid(false);
      }
    };
    validateToken();
  }, [token, type]);

  const validatePassword = (password) => {
    if (password.length < 12) {
      return "Password must be at least 12 characters long.";
    }
    if (password.length > 50) {
      return "Password must be 50 characters or fewer.";
    }
    if (!/[A-Z]/.test(password)) {
      return "Password must contain at least one uppercase letter.";
    }
    if (!/[a-z]/.test(password)) {
      return "Password must contain at least one lowercase letter.";
    }
    if (!/[0-9]/.test(password)) {
      return "Password must contain at least one number.";
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return "Password must contain at least one special character.";
    }
    return "";
  };

  const handlePasswordReset = async () => {
    const validationError = validatePassword(newPassword);
    if (validationError) {
      setPasswordError(validationError);
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage({ error: 'Passwords do not match', success: '' });
      return;
    }
    try {
      const endpoint = `${API_URL}/api/${type === 'team_member' ? 'reset_team_member_password' : 'reset_password'}`;
      const { data } = await axios.post(endpoint, { token, new_password: newPassword });
      setMessage({ error: '', success: data.message });
      setTimeout(() => navigate('/'), 3000);
    } catch (error) {
      setMessage({ error: error.response?.data?.message || 'An error occurred', success: '' });
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography component="h1" variant="h5">Reset Password</Typography>
        {message.error && <Alert severity="error" sx={{ mt: 2 }}>{message.error}</Alert>}
        {message.success && <Alert severity="success" sx={{ mt: 2 }}>{message.success}</Alert>}
        {passwordError && <Alert severity="warning" sx={{ mt: 2 }}>{passwordError}</Alert>}
        {isTokenValid && !message.success && (
          <Box component="form" onSubmit={(e) => { e.preventDefault(); handlePasswordReset(); }} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setPasswordError(''); // Clear the error when typing
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              Password must be 12-50 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character.
            </Typography>
            <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }}>
              Reset Password
            </Button>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default ResetPassword;
